import React, { useEffect } from "react";
import { Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Platform, View, Text } from "../components/common";
import { AppLayout } from "../components/layout";

// Import the manager screens
import { ManagerDashboardScreen } from "../screens/manager/ManagerDashboardScreen";
import { StoreVisitScreen } from "../screens/manager/StoreVisitScreen";
import { StoreDetailsScreen } from "../screens/manager/StoreDetailsScreen";
import { OrderManagementScreen } from "../screens/manager/OrderManagementScreen";
import { OrderDetailsScreen } from "../screens/manager/OrderDetailsScreen";
import { StoreManagementScreen } from "../screens/manager/StoreManagementScreen";
import { StoreImportScreen } from "../screens/manager/StoreImportScreen";
import { UserManagementScreen } from "../screens/manager/UserManagementScreen";
import { AnalyticsDashboardScreen } from "../screens/manager/AnalyticsDashboardScreen";
import { ResourceManagementScreen } from "../screens/manager/ResourceManagementScreen";
import { ResourceDetailsScreen } from "../screens/shared/ResourceDetailsScreen";
import { AnnouncementsListScreen } from "../screens/shared/AnnouncementsListScreen";
import { AnnouncementDetailScreen } from "../screens/shared/AnnouncementDetailScreen";
import { 
  StoreResourceScreen,
  RequestContactScreen,
  OrderSuppliesScreen
} from "../screens/manager/PlaceholderScreens";
import { ContestEntryScreen } from "../screens/manager/ContestEntryScreen";
import { PayrollScreen } from "../screens/manager/PayrollScreen";
import { ManagerProfileScreen } from "../screens/manager/ProfileScreen";
import { NotesMigrationScreen } from "../screens/manager/NotesMigrationScreen";
import { ShipmentManagementScreen } from "../screens/manager/ShipmentManagementScreen";
import { ProductManagementScreen } from "../screens/manager/ProductManagementScreen";
import { ShipmentsViewScreen } from "../screens/salesrep/ShipmentsViewScreen";
import { ResourcesScreen } from "../screens/salesrep/ResourcesScreen";
import { EosSurveyManagementScreen } from "../screens/manager/EosSurveyManagementScreen";
import { EosSurveyScreen } from "../screens/salesrep/EosSurveyScreen";
import { TradeshowSubmissionsScreen } from "../screens/manager/TradeshowSubmissionsScreen";
import { useAuth } from "../contexts/AuthContext";

// Component to handle redirect after login
const LoginRedirectHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const redirectTo = sessionStorage.getItem('loginRedirect');
    if (redirectTo && redirectTo.startsWith('/')) {
      sessionStorage.removeItem('loginRedirect');
      navigate(redirectTo, { replace: true });
      return;
    }
    // Default redirect to dashboard
    navigate('/manager/dashboard', { replace: true });
  }, [navigate]);
  
  return null;
};

// Wrapper for screens in web mode
const WebScreenWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <AppLayout>
      {children}
    </AppLayout>
  );
};

// Web components
const WebDashboard = (props: any) => {
  console.log("WebDashboard component rendering");
  return (
    <WebScreenWrapper>
      <ManagerDashboardScreen {...props} />
    </WebScreenWrapper>
  );
};

const WebProfile = (props: any) => (
  <WebScreenWrapper>
    <ManagerProfileScreen {...props} />
  </WebScreenWrapper>
);

const WebOrderDetails = () => {
  return (
    <WebScreenWrapper>
      <OrderDetailsScreen />
    </WebScreenWrapper>
  );
};

const MobileOrderDetails = () => {
  return (
    <AppLayout>
      <OrderDetailsScreen />
    </AppLayout>
  );
};

export const ManagerNavigator = () => {
  console.log("ManagerNavigator rendered");
  const { userRole } = useAuth();
  const isHandLead = userRole === 'hand-lead';
  
  // For web platform
  if (Platform.OS === "web") {
    console.log("ManagerNavigator - web platform detected");
    return (
      <Routes>
        <Route path="/login" element={<LoginRedirectHandler />} />
        <Route path="/manager" element={<WebDashboard />} />
        <Route path="/manager/dashboard" element={<WebDashboard />} />
        <Route path="/manager/profile" element={<WebProfile />} />
        
        {/* Management Screens */}
        <Route path="/manager/products" element={<WebScreenWrapper><ProductManagementScreen /></WebScreenWrapper>} />
        <Route path="/manager/orders" element={<WebScreenWrapper><OrderManagementScreen /></WebScreenWrapper>} />
        <Route path="/manager/order-details/:storeId/:orderId" element={<WebOrderDetails />} />
        <Route path="/manager/stores" element={<WebScreenWrapper><StoreManagementScreen /></WebScreenWrapper>} />
        <Route path="/manager/stores/import" element={<WebScreenWrapper><StoreImportScreen /></WebScreenWrapper>} />
        {/* User Management - only for managers, not hand-lead */}
        {!isHandLead && (
          <Route path="/manager/users" element={<WebScreenWrapper><UserManagementScreen /></WebScreenWrapper>} />
        )}
        <Route path="/manager/analytics" element={<WebScreenWrapper><AnalyticsDashboardScreen /></WebScreenWrapper>} />
        {/* Resources - different screens for hand-lead */}
        {isHandLead ? (
          <>
            <Route path="/resources" element={<WebScreenWrapper><ResourcesScreen /></WebScreenWrapper>} />
            <Route path="/resources/:resourceId" element={<WebScreenWrapper><ResourceDetailsScreen /></WebScreenWrapper>} />
          </>
        ) : (
          <>
            <Route path="/manager/resources" element={<WebScreenWrapper><ResourceManagementScreen /></WebScreenWrapper>} />
            <Route path="/manager/resources/:resourceId" element={<WebScreenWrapper><ResourceDetailsScreen /></WebScreenWrapper>} />
          </>
        )}
        <Route path="/manager/announcements" element={<WebScreenWrapper><AnnouncementsListScreen /></WebScreenWrapper>} />
        <Route path="/manager/announcements/:announcementId" element={<WebScreenWrapper><AnnouncementDetailScreen /></WebScreenWrapper>} />
        {/* Support announcement routes without /manager prefix for shared links */}
        <Route path="/announcements" element={<WebScreenWrapper><AnnouncementsListScreen /></WebScreenWrapper>} />
        <Route path="/announcements/:announcementId" element={<WebScreenWrapper><AnnouncementDetailScreen /></WebScreenWrapper>} />
        
        {/* Store Operations */}
        <Route path="/manager/store-visit" element={<WebScreenWrapper><StoreVisitScreen /></WebScreenWrapper>} />
        <Route path="/manager/store-details/:storeId" element={<WebScreenWrapper><StoreDetailsScreen /></WebScreenWrapper>} />
        
        {/* Tools and Resources */}
        <Route path="/manager/store-resource" element={<WebScreenWrapper><StoreResourceScreen /></WebScreenWrapper>} />
        <Route path="/manager/request-contact" element={<WebScreenWrapper><RequestContactScreen /></WebScreenWrapper>} />
        <Route path="/manager/order-supplies" element={<WebScreenWrapper><OrderSuppliesScreen /></WebScreenWrapper>} />
        <Route path="/manager/payroll" element={<WebScreenWrapper><PayrollScreen /></WebScreenWrapper>} />
        <Route path="/manager/contest-entry" element={<WebScreenWrapper><ContestEntryScreen /></WebScreenWrapper>} />
        {isHandLead ? (
          <Route path="/manager/eos-survey" element={<WebScreenWrapper><EosSurveyScreen /></WebScreenWrapper>} />
        ) : (
          <Route path="/manager/eos-survey" element={<WebScreenWrapper><EosSurveyManagementScreen /></WebScreenWrapper>} />
        )}
        <Route path="/manager/tradeshow" element={<WebScreenWrapper><TradeshowSubmissionsScreen /></WebScreenWrapper>} />
        
        {/* Admin Tools */}
        <Route path="/manager/migrate-notes" element={<WebScreenWrapper><NotesMigrationScreen /></WebScreenWrapper>} />
        
        {/* Shipment Management - different screens for hand-lead */}
        {isHandLead ? (
          <Route path="/shipments" element={<WebScreenWrapper><ShipmentsViewScreen /></WebScreenWrapper>} />
        ) : (
          <Route path="/manager/shipments" element={<WebScreenWrapper><ShipmentManagementScreen /></WebScreenWrapper>} />
        )}
        
        <Route path="/manager/*" element={<Navigate to="/manager/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/manager/dashboard" replace />} />
      </Routes>
    );
  }

  // For mobile platforms
  return (
    <Routes>
      <Route path="/login" element={<LoginRedirectHandler />} />
      <Route path="/manager" element={<AppLayout><ManagerDashboardScreen /></AppLayout>} />
      <Route path="/manager/dashboard" element={<AppLayout><ManagerDashboardScreen /></AppLayout>} />
      <Route path="/manager/profile" element={<AppLayout><ManagerProfileScreen /></AppLayout>} />
      
      {/* Management Screens */}
      <Route path="/manager/products" element={<AppLayout><ProductManagementScreen /></AppLayout>} />
      <Route path="/manager/orders" element={<AppLayout><OrderManagementScreen /></AppLayout>} />
      <Route path="/manager/order-details/:storeId/:orderId" element={<MobileOrderDetails />} />
      <Route path="/manager/stores" element={<AppLayout><StoreManagementScreen /></AppLayout>} />
      {/* User Management - only for managers, not hand-lead */}
      {!isHandLead && (
        <Route path="/manager/users" element={<AppLayout><UserManagementScreen /></AppLayout>} />
      )}
      <Route path="/manager/analytics" element={<AppLayout><AnalyticsDashboardScreen /></AppLayout>} />
      {/* Resources - different screens for hand-lead */}
      {isHandLead ? (
        <>
          <Route path="/resources" element={<AppLayout><ResourcesScreen /></AppLayout>} />
          <Route path="/resources/:resourceId" element={<AppLayout><ResourceDetailsScreen /></AppLayout>} />
        </>
      ) : (
        <>
          <Route path="/manager/resources" element={<AppLayout><ResourceManagementScreen /></AppLayout>} />
          <Route path="/manager/resources/:resourceId" element={<AppLayout><ResourceDetailsScreen /></AppLayout>} />
        </>
      )}
      <Route path="/manager/announcements" element={<AppLayout><AnnouncementsListScreen /></AppLayout>} />
      <Route path="/manager/announcements/:announcementId" element={<AppLayout><AnnouncementDetailScreen /></AppLayout>} />
      {/* Support announcement routes without /manager prefix for shared links */}
      <Route path="/announcements" element={<AppLayout><AnnouncementsListScreen /></AppLayout>} />
      <Route path="/announcements/:announcementId" element={<AppLayout><AnnouncementDetailScreen /></AppLayout>} />
      
      {/* Store Operations */}
      <Route path="/manager/store-visit" element={<AppLayout><StoreVisitScreen /></AppLayout>} />
      <Route path="/manager/store-details/:storeId" element={<AppLayout><StoreDetailsScreen /></AppLayout>} />
      
      {/* Tools and Resources */}
      <Route path="/manager/store-resource" element={<AppLayout><StoreResourceScreen /></AppLayout>} />
      <Route path="/manager/request-contact" element={<AppLayout><RequestContactScreen /></AppLayout>} />
      <Route path="/manager/order-supplies" element={<AppLayout><OrderSuppliesScreen /></AppLayout>} />
      <Route path="/manager/payroll" element={<AppLayout><PayrollScreen /></AppLayout>} />
      <Route path="/manager/contest-entry" element={<AppLayout><ContestEntryScreen /></AppLayout>} />
      {isHandLead ? (
        <Route path="/manager/eos-survey" element={<AppLayout><EosSurveyScreen /></AppLayout>} />
      ) : (
        <Route path="/manager/eos-survey" element={<AppLayout><EosSurveyManagementScreen /></AppLayout>} />
      )}
      <Route path="/manager/tradeshow" element={<AppLayout><TradeshowSubmissionsScreen /></AppLayout>} />
      
      {/* Admin Tools */}
      <Route path="/manager/migrate-notes" element={<AppLayout><NotesMigrationScreen /></AppLayout>} />
      
      {/* Shipment Management - different screens for hand-lead */}
      {isHandLead ? (
        <Route path="/shipments" element={<AppLayout><ShipmentsViewScreen /></AppLayout>} />
      ) : (
        <Route path="/manager/shipments" element={<AppLayout><ShipmentManagementScreen /></AppLayout>} />
      )}
      
      <Route path="/manager/*" element={<Navigate to="/manager/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/manager/dashboard" replace />} />
    </Routes>
  );
};
